import styled from "styled-components";
import { FC, useEffect, useState } from "react";
import MatchStats from "./MatchStats";
import { Fixture } from "../../../models/fixture";
import { Team } from "../../../models/team";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar, faEdit, faFutbol, faTrophy, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useLeague } from "../../../context/useLeague";
import { useUser } from "../../../context/useUser";
import SubmitScoreForm from "./SubmitScoreForm";
import SubmitStatsForm from "./SubmitStatsForm";
import ScoreCardReverse from "./ScoreCardReverse";
import FixtureService from '../../../services/fixture'
import HelperService from '../../../services/helper'
import useNotyf from "../../../context/useNotify";

interface Props {
  fixtureId: string
  team: Team
}

const ScoreCard: FC<Props> = ({ fixtureId, team }) => {
  const { user } = useUser()
  const notfy = useNotyf()

  // Data
  const [isFlipped, setIsFlipped] = useState(false);

  const [fixture, setFixture] = useState<Fixture>()
  const [isManager, setIsManager] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);

  const [displayHomeLineup, setDisplayHomeLineup] = useState(true);

  enum CardFace {
    STATS = "STATS",
    LINEUP = "LINEUP",
    SUBMIT_SCORE = "SUBMIT_SCORE",
    SUBMIT_STATS = "SUBMIT_STATS",
  }

  const [currentFace, setCurrentFace] = useState<CardFace>(CardFace.STATS);
  
  // Methods
  const handleFlip = (face: CardFace) => {
    setCurrentFace(face);
  };

  const toggleLineup = () => {
    setDisplayHomeLineup(!displayHomeLineup);
  };

  // API
  const fetchFixture = async (id: string) => {
    try {
      const fixture = await FixtureService.readFixture(id)
      setFixture(fixture)
    } catch (err) {
      console.log(err);
      notfy.error("Error fetching fixture")
    }
  }

  // Hooks 
  useEffect(() => {
    setIsManager(team?.managerId === user?._id);
    setIsPlayer(team?.roster.some(id => id === user?._id));
    fetchFixture(fixtureId)
  }, [user, team, fixtureId]);

  return (
    <Container>
      <FlipCard>
        {fixture ? 
          <FlipCardInner isFlipped={currentFace !== CardFace.STATS}>
          <FlipCardFront>
            {currentFace === CardFace.STATS && (
              <>
                <MatchStats fixture={fixture} team={team} />
                <Actions>
                {isManager && (
                  <ActionButton onClick={() => handleFlip(CardFace.SUBMIT_SCORE)}>
                    <FontAwesomeIcon icon={faTrophy} /> Submit Score
                  </ActionButton>
                )}
                {isPlayer && (
                  <ActionButton onClick={() => handleFlip(CardFace.SUBMIT_STATS)}>
                    <FontAwesomeIcon icon={faFutbol} /> Submit Stats
                  </ActionButton>
                )}
                <ActionButton onClick={() => handleFlip(CardFace.LINEUP)}>
                  <FontAwesomeIcon icon={faUsers} /> Lineup
                </ActionButton>
              </Actions>

              </>
            )}
          </FlipCardFront>
          <FlipCardBack>
            {currentFace === CardFace.LINEUP && (
                <ScoreCardReverse fixture={fixture} team={team} displayHomeLineup={displayHomeLineup} />
            )}
            {currentFace === CardFace.SUBMIT_SCORE && (
              <SubmitScoreForm fixture={fixture} team={team} refresh={ () => handleFlip(CardFace.STATS) } />
              )}
            {currentFace === CardFace.SUBMIT_STATS && (
                <SubmitStatsForm fixture={fixture} team={team} refresh={ () => handleFlip(CardFace.STATS) } />
            )}
              <Actions>
                <ActionButton onClick={() => handleFlip(CardFace.STATS)}>
                  <FontAwesomeIcon icon={faChartBar} /> Back to Stats
                </ActionButton>
                {currentFace === CardFace.LINEUP && (
                  <ActionButton onClick={toggleLineup}>
                    {displayHomeLineup ? "Show Away Lineup" : "Show Home Lineup"}
                  </ActionButton>
                )}
              </Actions>
          </FlipCardBack>
        </FlipCardInner>
        : null}
    </FlipCard>
  </Container>
  );
};
export default ScoreCard;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 85vw;
  max-width: 420px;
  height: 600px; // Set a fixed height
  margin: 0 auto;
  margin-bottom: 2rem;
  margin-top: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden; // Hide overflow

  @media (max-width: 768px) {
    width: 90vw;
  }

  @media (max-width: 360px) {
    width: 95vw;
  }
`;
const FlipCard = styled.div`
  width: 100%;
  height: 100%;
  perspective: 1000px;
`;

const FlipCardInner = styled.div<{ isFlipped: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${({ isFlipped }) => (isFlipped ? "rotateY(180deg)" : "none")};
`;

const FlipCardContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: absolute; // Ensures that front and back occupy the same space
  backface-visibility: hidden; // Hides the back of each side
`;

const FlipCardFront = styled(FlipCardContent)`
  z-index: 2; // Ensures front is on top by default
`;

const FlipCardBack = styled(FlipCardContent)`
  transform: rotateY(180deg);
  z-index: 1; // Back has lower z-index
`;


const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 0.5rem;
  margin-top: 1rem;
`;


const ActionButton = styled.button`
  border: 2px solid #5fa8e6;
  background: linear-gradient(120deg, #23263a 60%, #1a1c28 100%);
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 0 4px 0.5px #00eaff;
  }

  svg {
    margin-right: 5px;
  }
`;
